import type { Language, VodChapter, VodSlideMarker } from 'src/generated/graphql-manager';
import type { Slide } from 'src/viewer/components/slide-preview/slide-preview';

import { IconButton, InputDescription, PlayerIcon, TextInput, Tooltip } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import { SlideMarkerDropdown } from './slide-marker-dropdown';
import { TitleTranslations } from './title-translations';
import { offsetInMsToString } from '../event-summary-cue-points/format-time';

import styles from './event-summary-chapters.module.css';

type InputsGroupProps = {
  chapter: VodChapter;
  slideMarkers?: VodSlideMarker[];
  slides?: Slide[];
  index: number;
  primaryLangCode: string;
  languages: Language[];
  errorMessage?: string;
  onOffsetChange: (chapterId: VodChapter['id'], value: string) => void;
  onSetCurrentPlayerTime: (chapterId: VodChapter['id']) => void;
  onTitleChange: (chapterId: VodChapter['id'], value: string) => void;
  onTitlesUpdate: (chapterId: VodChapter['id'], titles: { language: string; text: string }[]) => void;
};

export function InputsGroup({
  chapter,
  slideMarkers,
  slides,
  index,
  primaryLangCode,
  languages,
  errorMessage,
  onOffsetChange,
  onSetCurrentPlayerTime,
  onTitleChange,
  onTitlesUpdate,
}: InputsGroupProps) {
  const { t } = useTranslation();

  const hasSlideMarkers = slideMarkers && slideMarkers?.length > 0;

  return (
    <div className={styles.inputsGroup}>
      <InputDescription
        className={styles.offsetInputLabel}
        inputId={`chapter-${index + 1}-offset`}
        label={t('manager.eventSummary.chapters.startTime')}
        helpMessage={!errorMessage && hasSlideMarkers ? t('manager.eventSummary.chapters.startTimeHelp') : '\u00A0'} // Empty space here makes the layout consistent whether there's an error or not
        errorMessage={errorMessage}
      >
        <TextInput
          data-testid={`chapter-${index + 1}-offset-input`}
          placeholder={t('manager.eventSummary.chapters.startTimePlaceholder')}
          value={chapter.offsetInMs ?? ''}
          onChange={(event) => onOffsetChange(chapter.id, event.target.value)}
        />

        <div className={styles.inputActions}>
          <Tooltip label={t('manager.eventSummary.chapters.setToCurrentPlayerTime')}>
            <IconButton
              className={styles.setCurrentTimeButton}
              data-testid={`chapter-${index + 1}-set-current-player-time-button`}
              aria-label={t('manager.eventSummary.chapters.setToCurrentPlayerTime')}
              naked
              small
              onClick={() => onSetCurrentPlayerTime(chapter.id)}
            >
              <PlayerIcon />
            </IconButton>
          </Tooltip>

          {hasSlideMarkers && (
            <SlideMarkerDropdown
              id={`chapter-${index + 1}`}
              slideMarkers={slideMarkers}
              slides={slides}
              onSelect={(slide) => onOffsetChange(chapter.id, offsetInMsToString(slide.offsetInMs, true))}
            />
          )}
        </div>
      </InputDescription>

      <InputDescription
        inputId={`chapter-${index + 1}-title`}
        label={t('manager.eventSummary.chapters.titleLabel')}
        className={styles.titleInputLabel}
      >
        <TextInput
          data-testid={`chapter-${index + 1}-title-input`}
          placeholder={t('manager.eventSummary.chapters.titleInputPlaceholder')}
          value={chapter.titles.find((title) => title.language === primaryLangCode)?.text ?? ''}
          onChange={(event) => onTitleChange(chapter.id, event.target.value)}
        />
      </InputDescription>

      {languages.length > 1 && (
        <TitleTranslations
          titles={chapter.titles}
          languages={languages}
          onSubmit={(titles) => onTitlesUpdate(chapter.id, titles)}
        />
      )}
    </div>
  );
}
